import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { haptic } from '../lib/haptics';
import {
  AuthGateShell,
  AuthUnderlineInput,
  AuthPasswordInput,
  AuthGoldButton,
} from './AuthGate';

// Google Identity Services — ID token flow keeps OAuth on helios.app's own
// domain (no redirect through supabase.co). Client ID is the same one from
// Google Cloud → Auth Platform → Clients → HELIOS Web.
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

type GoogleCredentialResponse = { credential: string };
type GoogleButtonConfig = {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: number;
};
type GoogleIdentityServices = {
  accounts: {
    id: {
      initialize: (config: {
        client_id: string;
        callback: (r: GoogleCredentialResponse) => void;
        nonce?: string;
        auto_select?: boolean;
        cancel_on_tap_outside?: boolean;
      }) => void;
      renderButton: (parent: HTMLElement, options: GoogleButtonConfig) => void;
      disableAutoSelect: () => void;
    };
  };
};
declare global {
  interface Window {
    google?: GoogleIdentityServices;
  }
}

async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const nonceRef = useRef<string>('');
  const googleBtnContainerRef = useRef<HTMLDivElement>(null);

  async function routeAfterLogin(userId: string | undefined) {
    if (!userId) { navigate('/app'); return; }
    const { data: profile } = await supabase
      .from('profiles')
      .select('age, goal, experience')
      .eq('id', userId)
      .maybeSingle();
    const onboardingComplete = profile && profile.age != null && profile.goal && profile.experience;
    haptic('prAchieved');
    navigate(onboardingComplete ? '/app' : '/onboarding');
  }

  async function handleGoogleCredential(response: GoogleCredentialResponse) {
    setError(null);
    const { data, error: signInError } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: response.credential,
      nonce: nonceRef.current,
    });
    nonceRef.current = '';
    if (signInError) {
      haptic('actionFailed');
      setError(signInError.message);
      return;
    }
    await routeAfterLogin(data.user?.id);
  }

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    (async () => {
      const start = Date.now();
      while (!window.google?.accounts?.id && Date.now() - start < 5000) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (cancelled || !window.google?.accounts?.id || !googleBtnContainerRef.current) return;
      const nonce = crypto.randomUUID();
      nonceRef.current = nonce;
      const hashedNonce = await sha256Hex(nonce);
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
        nonce: hashedNonce,
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      googleBtnContainerRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleBtnContainerRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        logo_alignment: 'left',
        width: 320,
      });
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAppleLogin() {
    setError(null);
    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'apple',
      options: { redirectTo: `${window.location.origin}/app` },
    });
    if (oauthError) {
      haptic('actionFailed');
      setError(oauthError.message);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    if (signInError) {
      console.error('[Login] signInWithPassword error:', signInError);
      setLoading(false);
      haptic('actionFailed');
      setError(signInError.message);
      return;
    }
    try {
      const user = signInData?.user;
      if (!user) {
        // Session not established — likely email not confirmed
        haptic('actionFailed');
        setError('Sign-in succeeded but no session was created. Check that your email is confirmed.');
        return;
      }
      const { data: profile } = await supabase
        .from('profiles')
        .select('age, goal, experience')
        .eq('id', user.id)
        .maybeSingle();

      const onboardingComplete =
        profile &&
        profile.age != null &&
        profile.goal &&
        profile.experience;

      haptic('prAchieved');
      if (onboardingComplete) {
        navigate('/app');
      } else {
        navigate('/onboarding');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotSubmit(e: React.FormEvent) {
    e.preventDefault();
    setForgotLoading(true);
    await supabase.auth.resetPasswordForEmail(forgotEmail, {
      redirectTo: window.location.origin + '/reset-password',
    });
    setForgotLoading(false);
    setForgotSent(true);
  }

  const socialBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    maxWidth: '320px',
    height: '52px',
    borderRadius: '14px',
    background: 'var(--color-text-primary)',
    border: 'none',
    color: 'var(--color-canvas)',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  const emailBtnStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '320px',
    height: '52px',
    borderRadius: '14px',
    background: 'transparent',
    border: '1px solid var(--color-border)',
    color: 'var(--color-ink-1)',
    fontSize: '15px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  };

  return (
    <AuthGateShell
      heading="Welcome back"
      footer={
        <p className="font-body text-sm text-[var(--color-text-secondary)]">
          Don&apos;t have an account?{' '}
          <Link
            to="/signup"
            className="text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-cinder)]"
          >
            Sign up
          </Link>
        </p>
      }
    >
      <div className="mx-auto w-full min-w-0 max-w-sm flex flex-col items-center" style={{ gap: '10px' }}>
        {/* Google — rendered by Google Identity Services. Fixed height to match
            the custom Apple button so spacing stays consistent while GIS loads. */}
        <div
          ref={googleBtnContainerRef}
          style={{
            width: '100%',
            maxWidth: '320px',
            minHeight: '52px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        />
        {!GOOGLE_CLIENT_ID && (
          <p className="font-body text-xs" style={{ color: 'var(--color-status-critical)' }}>
            Google sign-in unavailable (missing config).
          </p>
        )}

        {/* Apple */}
        <button type="button" style={socialBtnStyle} onClick={handleAppleLogin}>
          <svg viewBox="0 0 24 24" width="18" height="18" fill="var(--color-canvas)">
            <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
          </svg>
          Continue with Apple
        </button>

        {/* Separator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '4px 0', width: '100%', maxWidth: '320px' }}>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
          <span style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>or</span>
          <div style={{ flex: 1, height: '1px', background: 'rgba(255,255,255,0.08)' }} />
        </div>

        {/* Email toggle */}
        <button
          type="button"
          onClick={() => { setEmailExpanded((v) => !v); setForgotMode(false); setForgotSent(false); }}
          style={emailBtnStyle}
        >
          {emailExpanded ? 'Hide email sign in' : 'Continue with email'}
        </button>

        {/* Expandable email form */}
        <AnimatePresence initial={false}>
          {emailExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              style={{ overflow: 'hidden', width: '100%', maxWidth: '320px' }}
            >
              <div className="space-y-4 pt-1">
                {forgotMode ? (
                  forgotSent ? (
                    <p className="font-body text-sm text-center" style={{ color: 'var(--color-text-secondary)' }}>
                      Check your email for a reset link.
                    </p>
                  ) : (
                    <form onSubmit={handleForgotSubmit} className="space-y-4">
                      <AuthUnderlineInput
                        type="email"
                        placeholder="Email"
                        value={forgotEmail}
                        onChange={setForgotEmail}
                        autoComplete="email"
                        enterKeyHint="send"
                        error={null}
                      />
                      <AuthGoldButton disabled={forgotLoading}>
                        {forgotLoading ? 'Sending…' : 'Send reset link'}
                      </AuthGoldButton>
                      <button
                        type="button"
                        onClick={() => setForgotMode(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--color-text-secondary)',
                          fontSize: '12px',
                          width: '100%',
                          textAlign: 'center',
                          fontFamily: 'inherit',
                        }}
                      >
                        Back to sign in
                      </button>
                    </form>
                  )
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <p className="font-body rounded-lg border border-[var(--color-status-critical)]/30 bg-[var(--color-status-critical)]/10 px-3 py-2 text-xs text-[var(--color-status-critical)]">
                        {error}
                      </p>
                    )}
                    <AuthUnderlineInput
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={setEmail}
                      autoComplete="email"
                      enterKeyHint="next"
                      error={null}
                    />
                    <AuthPasswordInput
                      placeholder="Password"
                      value={password}
                      onChange={setPassword}
                      autoComplete="current-password"
                      enterKeyHint="go"
                      error={null}
                    />
                    <button
                      type="button"
                      onClick={() => { setForgotMode(true); setForgotEmail(email); }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'var(--color-text-secondary)',
                        fontSize: '12px',
                        padding: 0,
                        fontFamily: 'inherit',
                        display: 'block',
                      }}
                    >
                      Forgot password?
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      style={{
                        width: '100%',
                        height: '52px',
                        borderRadius: '14px',
                        background: 'var(--color-text-primary)',
                        border: 'none',
                        color: 'var(--color-canvas)',
                        fontSize: '15px',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        opacity: loading ? 0.5 : 1,
                        fontFamily: 'inherit',
                      }}
                    >
                      {loading ? 'Signing in…' : 'Sign in'}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AuthGateShell>
  );
}
